import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { hideVisually, lighten, rgba } from 'polished';

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  /* Hide checkbox visually but remain accessible to screen readers. */
  ${hideVisually()}
`;

const Label = styled.label`
  cursor: pointer;
  display: inline-flex;
  align-items: center;

  > span {
    margin-left: 0.33rem;
    font-size: 1rem;
  }
`;

const Icon = styled.svg`
  display: block;
  fill: none;
  stroke: white;
  stroke-width: 4px;
`;

const StyledCheckbox = styled.div`
  display: inline-block;
  position: relative;
  width: 16px;
  height: 16px;
  border: 1px solid ${({ theme }) => theme.palette.primary[0]};
  background: ${({ theme, checked }) => (checked ? theme.palette.primary[0] : 'white')};
  border-radius: 3px;
  transition: all ${({ theme }) => theme.anim.dur};

  &::after {
    opacity: 0;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: -1;
    content: "";
    border-radius: 3px;
    box-shadow: 0 0 8px 2px ${({ theme }) => rgba(theme.palette.primary[0], 0.5)};
    transition: all ${({ theme }) => theme.anim.dur};
  }

  ${Label}:hover &::after {
    opacity: 1;
  }

  ${HiddenCheckbox}:focus + & {
    box-shadow: 0 0 0 3px ${({ theme }) => lighten(0.5, theme.palette.primary[0])};
  }

  ${Icon} {
    visibility: ${(props) => (props.checked ? 'visible' : 'hidden')}
  }
`;

const CheckboxContainer = styled.div`
  display: inline-block;
  height: 18px;
`;

const Checkbox = ({
  className, checked, label, ...props
}) => (
  <Label>
    <CheckboxContainer className={className}>
      <HiddenCheckbox checked={checked} {...props} />
      <StyledCheckbox checked={checked}>
        <Icon viewBox="0 0 24 24">
          <polyline points="20 6 9 17 4 12" />
        </Icon>
      </StyledCheckbox>
    </CheckboxContainer>
    <span>{label}</span>
  </Label>
);

Checkbox.propTypes = {
  className: PropTypes.string,
  checked: PropTypes.bool.isRequired,
  label: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

Checkbox.defaultProps = {
  className: '',
  label: '',
};

export default Checkbox;
